import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { setModalState, modalViewsConst } from "../../store/modalSlice";
import { createProjectWithTasks } from "../../store/projectSlice";
import ModalBase from "./ModalBase";
import { ReactComponent as BriefcaseIcon } from "../../assets/icons/briefcase-02.svg";
import ProjectLoadingState from "../../pages/Projects/ProjectLoadingState";
import { ImageUploadTrigger } from "../imageUploader/ImageUploadFlow";
import { imageCache } from "../../utils/imageCache";
import type { AppDispatch } from "../../store";
import type { ProjectImageUploadResult } from "../imageUploader/imageUploadModals/ProjectImageUploadModal";

interface AddProjectModalProps {
  onClose: (result?: { success: boolean } | null) => void;
  // Restored across the image-picker handoff so typed input survives the round-trip
  initialTitle?: string;
  initialDescription?: string;
  initialImageUrl?: string | null;
  // Image the picker uploaded, forwarded back via __returnTo
  returnedResult?: ProjectImageUploadResult;
}

const AddProjectModal: React.FC<AddProjectModalProps> = ({
  onClose,
  initialTitle,
  initialDescription,
  initialImageUrl,
  returnedResult,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [title, setTitle] = useState(initialTitle ?? "");
  const [description, setDescription] = useState(initialDescription ?? "");
  const [imageUrl, setImageUrl] = useState<string | null>(initialImageUrl ?? null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const imageAppliedRef = useRef(false);

  // Apply the freshly uploaded image handed back from the picker, then fetch its preview
  useEffect(() => {
    if (imageAppliedRef.current) return;
    if (returnedResult?.action === "updateImage" && returnedResult?.success && returnedResult?.imageUrl) {
      imageAppliedRef.current = true;
      const uploadedPath = returnedResult.imageUrl;
      setImageUrl(uploadedPath);
      imageCache.getSignedUrl(uploadedPath).then((url) => {
        if (url) setImagePreviewUrl(url);
      });
    }
  }, [returnedResult]);

  // Restore the preview for an image carried across a cancelled picker round-trip
  useEffect(() => {
    const hasFreshUpload = returnedResult?.action === "updateImage" && returnedResult?.success;
    if (initialImageUrl && !hasFreshUpload) {
      imageCache.getSignedUrl(initialImageUrl).then((url) => {
        if (url) setImagePreviewUrl(url);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isCreating) return;

    setCurrentStep(0);
    setCompletedSteps([]);

    const timings = [0, 800, 1800, 2800];
    const timers = timings.map((delay, i) =>
      setTimeout(() => {
        setCurrentStep(i);
        setCompletedSteps((prev) => (i > 0 ? [...prev, i - 1] : prev));
      }, delay)
    );

    return () => timers.forEach(clearTimeout);
  }, [isCreating]);

  const handleClose = () => {
    if (isCreating) return;
    onClose(null);
  };

  // Reopen this modal (preserving typed input) once the picker closes — single-modal system has no stack
  const returnToSelf = useMemo(() => ({
    modalView: modalViewsConst.addProject,
    properties: {
      __modalSize: "sm",
      ...(title && { initialTitle: title }),
      ...(description && { initialDescription: description }),
      ...(imageUrl && { initialImageUrl: imageUrl }),
    },
  }), [title, description, imageUrl]);

  const handleOpenImageModal = () => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.projectImageUpload,
      properties: {
        id: null,
        name: title || "New Project",
        imageUrl: imageUrl || null,
        organizationId: null,
        __returnTo: returnToSelf,
      },
    }));
  };

  const handleCreate = async () => {
    if (!title.trim() || !description.trim()) return;

    setIsCreating(true);

    try {
      await dispatch(createProjectWithTasks({
        title: title.trim(),
        description: description.trim(),
        imageUrl: imageUrl ?? undefined,
      })).unwrap();

      onClose({ success: true });
    } catch (err) {
      console.error('[AddProjectModal] Failed to create project:', err);
      setIsCreating(false);
    }
  };

  const isValid = title.trim().length > 0 && description.trim().length > 0;

  return (
    <ModalBase
      title={isCreating ? "Getting things ready..." : "Create a Project"}
      icon={<BriefcaseIcon className="w-5 h-5 stroke-eai-primary-accent" />}
      onClose={handleClose}
      size="sm"
      footer={
        isCreating ? undefined : (
          <>
            <button
              onClick={handleClose}
              className="eai-btn-outline flex-1"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={!isValid}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isValid ? "eai-btn-grn" : "eai-btn-disabled"
              }`}
            >
              Create Project
            </button>
          </>
        )
      }
    >
      {isCreating ? (
        <ProjectLoadingState
          currentStep={currentStep}
          completedSteps={completedSteps}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="eai-meta-label">Project Name</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              placeholder="e.g. Wind Farm Development"
              className="eai-input"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="eai-meta-label">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              placeholder="Describe your project. The more detail you add, the better the AI task suggestions will be."
              rows={4}
              className="eai-input resize-none"
            />
          </div>

          <ImageUploadTrigger
            label="Project Image (Optional)"
            previewUrl={imagePreviewUrl}
            onClick={handleOpenImageModal}
          />

          <div className="bg-eai-accent-lt rounded-md px-3 py-2 text-xs text-eai-ink-subtle">
            💡 AI will suggest 5 tasks based on your project description to help you get started.
          </div>
        </div>
      )}
    </ModalBase>
  );
};

export default AddProjectModal;
